import { useState, useEffect, useRef } from 'react';
import { loginAgent, logoutAgent, isAgentAuthenticated, getAgentToken } from '../../services/agentApi';
import { getRecentChats, getOrderMessagesForAgent } from '../../services/chatApi';
import agentWebSocketService from '../../services/agentWebSocket';
import './AgentPanel.css';

/**
 * Панель агента поддержки
 */
const AgentPanel = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Agent data
  const [agentData, setAgentData] = useState(null);
  
  // Chat state
  const [recentChats, setRecentChats] = useState([]);
  const [archivedChats, setArchivedChats] = useState([]);
  const [activeTab, setActiveTab] = useState('recent'); // 'recent' or 'archive'
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [currentOrder, setCurrentOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isApiConnected, setIsApiConnected] = useState(false);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  
  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const authenticated = isAgentAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        const savedAgentData = localStorage.getItem('agentData');
        if (savedAgentData) {
          setAgentData(JSON.parse(savedAgentData));
        }
        
        // Load recent chats from API
        await loadRecentChats();
        
        // Initialize WebSocket
        initializeWebSocket();
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
    
    return () => {
      agentWebSocketService.disconnect();
    };
  }, []);
  
  /**
   * Load recent chats from API
   */
  const loadRecentChats = async () => {
    try {
      console.log('Loading recent chats from API...');
      const chats = await getRecentChats(50);
      console.log('Loaded recent chats:', chats.length);
      setRecentChats(chats);
      setIsApiConnected(true); // API call successful
    } catch (error) {
      console.error('Error loading recent chats:', error);
      setIsApiConnected(false);
      
      // Check if it's 401 or 404
      if (error.status === 401 || error.status === 404) {
        console.error('Authentication failed or resource not found');
        
        // If 401, token might be expired - logout
        if (error.status === 401) {
          console.error('Token expired or invalid - logging out');
          setTimeout(() => {
            handleLogout();
          }, 2000);
        }
      }
    }
  };
  
  /**
   * Update connection status based on API and WebSocket
   */
  useEffect(() => {
    setIsConnected(isApiConnected && isWebSocketConnected);
  }, [isApiConnected, isWebSocketConnected]);
  
  /**
   * Initialize WebSocket connection according to documentation
   */
  const initializeWebSocket = () => {
    const token = getAgentToken();
    if (!token) {
      console.error('No token found for WebSocket authentication');
      return;
    }
    
    console.log('Connecting to WebSocket...');
    
    // Connect to WebSocket (port 3001 according to docs)
    const socket = agentWebSocketService.connect();
    
    // Connection events
    socket.on('connect', () => {
      console.log('✅ WebSocket connected:', socket.id);
      
      // Authenticate with WebSocket after connection
      console.log('Authenticating with token...');
      socket.emit('agent:authenticate', { token });
    });
    
    socket.on('disconnect', () => {
      console.log('❌ WebSocket disconnected');
      setIsWebSocketConnected(false);
    });
    
    socket.on('connect_error', (error) => {
      console.error('❌ WebSocket connection error:', error);
      setIsWebSocketConnected(false);
    });
    
    // Authentication events
    socket.on('auth:success', (data) => {
      console.log('✅ WebSocket authenticated:', data.agent.email);
      console.log('Agent role:', data.agent.role);
      setIsWebSocketConnected(true);
    });
    
    socket.on('auth:error', (data) => {
      console.error('❌ WebSocket auth error:', data.message);
      setLoginError('WebSocket authentication failed. Please try logging in again.');
      setIsWebSocketConnected(false);
    });
    
    // Receive recent chats list (automatically sent after auth)
    socket.on('chats:recent', (chats) => {
      console.log('Received recent chats from WebSocket:', chats.length);
      // Merge with existing chats from API
      setRecentChats(prevChats => {
        const chatMap = new Map();
        
        // Add existing chats
        prevChats.forEach(chat => chatMap.set(chat.id, chat));
        
        // Add/update with new chats from WebSocket
        chats.forEach(chat => chatMap.set(chat.id, chat));
        
        // Convert back to array and sort by date
        return Array.from(chatMap.values()).sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
      });
    });
    
    // Receive user messages
    socket.on('user:message', (data) => {
      console.log('New message from user:', data);
      console.log('Email:', data.email);
      console.log('Order:', data.orderId);
      console.log('Message:', data.message);
      
      // Add message if we're in this chat
      if (currentOrder?.id === data.orderId) {
        const newMessage = {
          id: Date.now(),
          text: data.message,
          isFromUser: true,
          timestamp: new Date(data.timestamp || Date.now()),
          email: data.email,
        };
        setMessages(prev => [...prev, newMessage]);
      }
      
      // Show notification
      if (Notification.permission === 'granted') {
        new Notification('New message from user', {
          body: `${data.email}: ${data.message}`,
          icon: '/favicon.ico'
        });
      }
    });
    
    // User status events
    socket.on('user:joined', (data) => {
      console.log('User joined:', data.email);
      console.log('To order:', data.orderId);
    });
    
    socket.on('user:left', (data) => {
      console.log('User left order:', data.orderId);
    });
    
    // New order notifications
    socket.on('order:new', (orderData) => {
      console.log('New order created:', orderData);
      if (Notification.permission === 'granted') {
        new Notification('New Order Created!', {
          body: `Order from ${orderData.email}`,
          icon: '/favicon.ico'
        });
      }
    });
    
    // Order status changes
    socket.on('order:status-changed', (data) => {
      console.log('Order status changed:', data);
      // Update order status if we're viewing this order
      if (currentOrder?.id === data.orderId) {
        setCurrentOrder(prev => ({ ...prev, status: data.status }));
      }
    });
    
    // Message sent confirmation
    socket.on('message:sent', (data) => {
      console.log('Message sent confirmation:', data);
    });
    
    // Error handling
    socket.on('error', (data) => {
      console.error('WebSocket error:', data.message);
    });
  };
  
  /**
   * Handle login
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);
    
    try {
      const data = await loginAgent(email, password);
      setAgentData(data.agent);
      setIsAuthenticated(true);
      
      // Load recent chats after successful login
      await loadRecentChats();
      
      // Initialize WebSocket
      initializeWebSocket();
    } catch (error) {
      setLoginError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Handle logout
   */
  const handleLogout = () => {
    logoutAgent();
    agentWebSocketService.disconnect();
    setIsAuthenticated(false);
    setAgentData(null);
    setRecentChats([]);
    setArchivedChats([]);
    setCurrentOrder(null);
    setMessages([]);
    setIsConnected(false);
    setIsApiConnected(false);
    setIsWebSocketConnected(false);
  };
  
  /**
   * Join order chat - load messages via API and connect via WebSocket
   */
  const handleJoinOrder = async (chat) => {
    if (!chat.orderId) {
      console.error('No orderId in chat:', chat);
      return;
    }
    
    console.log('Joining order chat:', chat.orderId);
    console.log('Chat data:', chat);
    
    try {
      // 1. Load messages from API
      console.log('Loading messages from API for order:', chat.orderId);
      const messages = await getOrderMessagesForAgent(chat.orderId);
      console.log('Loaded messages:', messages.length);
      
      // 2. Set current order (use chat.order or create from chat data)
      const orderData = chat.order || {
        id: chat.orderId,
        email: chat.email || 'Unknown',
        status: chat.status || 'PENDING',
        amount: chat.amount || 0,
        createdAt: chat.createdAt
      };
      setCurrentOrder(orderData);
      
      // 3. Format and set messages
      const formattedMessages = messages.map(msg => ({
        id: msg.id,
        text: msg.message,
        isFromUser: msg.isFromUser,
        timestamp: new Date(msg.createdAt),
        email: msg.isFromUser ? orderData.email : null,
        agent: msg.agent
      }));
      setMessages(formattedMessages);
      
      // 4. Connect to WebSocket for real-time updates
      const socket = agentWebSocketService.getSocket();
      if (socket && socket.connected) {
        console.log('Connecting to WebSocket for order:', chat.orderId);
        socket.emit('agent:join-order', {
          orderId: chat.orderId
        });
      } else {
        console.warn('WebSocket not connected, only showing API messages');
      }
      
    } catch (error) {
      console.error('Error joining order chat:', error);
      
      // Show error to user
      if (error.status === 401) {
        setLoginError('Session expired. Please login again.');
        setTimeout(() => handleLogout(), 2000);
      } else {
        alert('Failed to load chat messages. Please try again.');
      }
    }
  };
  
  /**
   * Send message to user via WebSocket
   */
  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || !currentOrder) {
      console.warn('Cannot send message: empty input or no order selected');
      return;
    }
    
    const socket = agentWebSocketService.getSocket();
    if (!socket || !socket.connected) {
      console.error('WebSocket not connected');
      alert('Cannot send message: WebSocket not connected. Please reconnect.');
      return;
    }
    
    const messageText = inputMessage.trim();
    const orderId = currentOrder.id;
    
    console.log('Sending message to order:', orderId);
    console.log('Message:', messageText);
    
    // Send message via WebSocket with orderId
    socket.emit('agent:message', {
      orderId: orderId,
      message: messageText
    });
    
    // Add message to UI immediately (optimistic update)
    const newMessage = {
      id: Date.now(),
      text: messageText,
      isFromUser: false,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
  };
  
  /**
   * Request notification permission
   */
  useEffect(() => {
    if (isAuthenticated && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [isAuthenticated]);
  
  /**
   * Close context menu when clicking outside
   */
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);
  
  /**
   * Handle right click on chat item
   */
  const handleChatContextMenu = (e, chat) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      chat: chat,
      isArchived: activeTab === 'archive'
    });
  };
  
  /**
   * Move chat to archive
   */
  const moveToArchive = (chat) => {
    setRecentChats(prev => prev.filter(c => c.id !== chat.id));
    setArchivedChats(prev => [...prev, { ...chat, archivedAt: new Date() }]);
    setContextMenu(null);
    
    // If this was the current order, clear it
    if (currentOrder?.id === chat.orderId) {
      setCurrentOrder(null);
      setMessages([]);
    }
  };
  
  /**
   * Restore chat from archive
   */
  const restoreFromArchive = (chat) => {
    setArchivedChats(prev => prev.filter(c => c.id !== chat.id));
    const { archivedAt, ...chatWithoutArchive } = chat;
    setRecentChats(prev => [chatWithoutArchive, ...prev].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    ));
    setContextMenu(null);
  };
  
  /**
   * Get sorted chats based on current sort order
   */
  const getSortedChats = (chats) => {
    return [...chats].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
  };
  
  /**
   * Get current chat list based on active tab
   */
  const getCurrentChats = () => {
    const chats = activeTab === 'recent' ? recentChats : archivedChats;
    return getSortedChats(chats);
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="agent-panel">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  
  // Login form
  if (!isAuthenticated) {
    return (
      <div className="agent-panel">
        <div className="login-container">
          <div className="login-box">
            <h1 className="login-title">Support Agent Login</h1>
            <p className="login-subtitle">Access the support dashboard</p>
            
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="agent@example.com"
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                />
              </div>
              
              {loginError && (
                <div className="error-message">
                  {loginError}
                </div>
              )}
              
              <button 
                type="submit" 
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }
  
  // Main agent panel
  return (
    <div className="agent-panel">
      {/* Header */}
      <div className="agent-header">
        <div className="agent-info">
          <h1>Support Dashboard</h1>
          <div className="agent-details">
            <span className="agent-name">{agentData?.name || agentData?.email}</span>
            <span className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
            </span>
            {!isConnected && (
              <button onClick={initializeWebSocket} className="reconnect-button">
                Reconnect
              </button>
            )}
          </div>
        </div>
        <button onClick={handleLogout} className="logout-button">
          Logout
        </button>
      </div>
      
      <div className="agent-content">
        {/* Sidebar with chats */}
        <div className="chats-sidebar">
          {/* Tabs */}
          <div className="chat-tabs">
            <button 
              className={`chat-tab ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
            >
              Recent ({recentChats.length})
            </button>
            <button 
              className={`chat-tab ${activeTab === 'archive' ? 'active' : ''}`}
              onClick={() => setActiveTab('archive')}
            >
              Archive ({archivedChats.length})
            </button>
          </div>
          
          {/* Sort controls */}
          <div className="chat-sort">
            <button 
              className={`sort-button ${sortOrder === 'newest' ? 'active' : ''}`}
              onClick={() => setSortOrder('newest')}
              title="Newest first"
            >
              ↓ Newest
            </button>
            <button 
              className={`sort-button ${sortOrder === 'oldest' ? 'active' : ''}`}
              onClick={() => setSortOrder('oldest')}
              title="Oldest first"
            >
              ↑ Oldest
            </button>
          </div>
          
          {/* Chat list */}
          <div className="chats-list">
            {getCurrentChats().length === 0 ? (
              <div className="no-chats">
                <p>{activeTab === 'recent' ? 'No recent chats' : 'No archived chats'}</p>
              </div>
            ) : (
              getCurrentChats().map((chat) => (
                <div
                  key={chat.id}
                  className={`chat-item compact ${activeTab === 'archive' ? 'archived' : ''} ${currentOrder?.id === chat.orderId ? 'active' : ''}`}
                  onClick={() => handleJoinOrder(chat)}
                  onContextMenu={(e) => handleChatContextMenu(e, chat)}
                >
                  <div className="chat-info">
                    <div className="chat-email">
                      {chat.order?.email || chat.email || 'Unknown User'}
                    </div>
                    <div className="chat-message">
                      {chat.message || 'No message'}
                    </div>
                  </div>
                  <div className="chat-meta">
                    <span className={`status-badge ${chat.order?.status?.toLowerCase() || 'pending'}`}>
                      {chat.order?.status || 'PENDING'}
                    </span>
                    <div className="chat-time">
                      {new Date(chat.createdAt).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Context Menu */}
        {contextMenu && (
          <div 
            className="context-menu"
            style={{
              position: 'fixed',
              top: contextMenu.y,
              left: contextMenu.x,
              zIndex: 1000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {contextMenu.isArchived ? (
              <button 
                className="context-menu-item"
                onClick={() => restoreFromArchive(contextMenu.chat)}
              >
                Restore from archive?
              </button>
            ) : (
              <button 
                className="context-menu-item"
                onClick={() => moveToArchive(contextMenu.chat)}
              >
                Move to archive?
              </button>
            )}
          </div>
        )}
        
        {/* Main chat area */}
        <div className="chat-area">
          {currentOrder ? (
            <>
              {/* Order info */}
              <div className="order-info">
                <h3>Order: {currentOrder.id}</h3>
                <div className="order-details">
                  <span>Email: {currentOrder.email}</span>
                  <span>Status: {currentOrder.status}</span>
                  <span>Amount: ${currentOrder.finalAmount || currentOrder.amount}</span>
                  <span>Created: {new Date(currentOrder.createdAt).toLocaleString()}</span>
                </div>
              </div>
              
              {/* Messages */}
              <div className="messages-container">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.isFromUser ? 'user-message' : 'agent-message'}`}
                  >
                    <div className="message-content">
                      {msg.isFromUser && msg.email && (
                        <div className="message-sender">{msg.email}</div>
                      )}
                      <div className="message-text">{msg.text}</div>
                      <div className="message-time">
                        {msg.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Message input */}
              <form onSubmit={handleSendMessage} className="message-input-form">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type your response..."
                  className="message-input"
                  disabled={!isConnected}
                />
                <button 
                  type="submit" 
                  className="send-button"
                  disabled={!isConnected || !inputMessage.trim()}
                >
                  Send
                </button>
              </form>
            </>
          ) : (
            <div className="no-order-selected">
              <h3>Select a chat to start</h3>
              <p>Choose a chat from the sidebar to view messages and respond to users.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentPanel;
